/**
 * Plan-ready notifications the shopper already acted on OUTSIDE the app —
 * dismissed or clicked through on the storefront banner
 * (extensions/fg-fuel-plans-theme/blocks/plan_notifications.liquid).
 *
 * The storefront banner can't advance the server seen-watermark (no CSRF
 * token on a theme page), so without this the app would toast "Your fueling
 * plan is ready" again on the next visit for a plan the shopper has already
 * dismissed — or is literally looking at after clicking "View plan →". The
 * banner records the requestId here (localStorage: shared across tabs, same
 * origin) and the app's drain settles those rows silently (bus + refetch +
 * watermark still happen; only the toast is suppressed).
 *
 * Key + shape are a contract with the Liquid block's inline script. Entries
 * self-expire after 30 days so the map can't grow forever.
 */

export const PLAN_NOTIFICATION_ACK_KEY = "fg:plan-generation:acknowledged";

/** Entries older than this are pruned on read/write — 30 days. */
export const PLAN_NOTIFICATION_ACK_TTL_MS = 30 * 24 * 60 * 60 * 1000;

type AckMap = Record<string, number>;

type StorageLike = Pick<Storage, "getItem" | "setItem">;

const defaultStorage = (): StorageLike | null =>
  typeof localStorage !== "undefined" ? localStorage : null;

function readMap(storage: StorageLike | null, now: number): AckMap {
  if (!storage) return {};
  try {
    const raw = storage.getItem(PLAN_NOTIFICATION_ACK_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const out: AckMap = {};
    for (const [id, at] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof at === "number" && now - at < PLAN_NOTIFICATION_ACK_TTL_MS) out[id] = at;
    }
    return out;
  } catch {
    return {};
  }
}

/** True when the storefront banner for this request was dismissed or clicked. */
export function isPlanNotificationAcknowledged(
  requestId: string,
  storage: StorageLike | null | undefined = defaultStorage(),
  now: number = Date.now()
): boolean {
  return Boolean(readMap(storage ?? null, now)[requestId]);
}

/** Record an acknowledgement (best-effort; prunes expired entries). */
export function acknowledgePlanNotification(
  requestId: string,
  storage: StorageLike | null | undefined = defaultStorage(),
  now: number = Date.now()
): void {
  if (!storage || !requestId) return;
  try {
    const map = readMap(storage, now);
    map[requestId] = now;
    storage.setItem(PLAN_NOTIFICATION_ACK_KEY, JSON.stringify(map));
  } catch {
    /* quota / private mode — a re-toast is the worst case */
  }
}
